import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { KeyRound, History, Plus, Trash2, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const isStrongPassword = (pw) => pw.length >= 8 && /[A-Z]/.test(pw) && /[^A-Za-z0-9]/.test(pw);

const FIELD_TYPES = [
  { v: "designation_id", l: "Designation", ref: "designations" },
  { v: "department_id", l: "Department", ref: "departments" },
  { v: "position", l: "Position" },
  { v: "grade", l: "Grade (needs SA approval)" },
  { v: "reporting_manager", l: "Reporting Officer", ref: "employees" },
  { v: "additional_charge", l: "Additional Charge / Also Responsible For" },
  { v: "employment_type", l: "Employment Type" },
  { v: "employment_status", l: "Employment Status (needs SA approval)" },
  { v: "project_site", l: "Project / Site" },
];

export default function AdminEmployeeHR({ employee, onChange }) {
  const eid = employee.id;
  const qc = useQueryClient();

  // login
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginPassword, setLoginPassword] = useState("");
  const [createdLogin, setCreatedLogin] = useState(null);

  // reset password
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState("");

  // hr change
  const [changeOpen, setChangeOpen] = useState(false);
  const [ch, setCh] = useState({ field_type: "", new_value: "", display_value: "", effective_from: "", reason: "" });

  // responsibilities / authorizations / qualifications dialogs
  const [respText, setRespText] = useState("");
  const [authForm, setAuthForm] = useState({ activity: "", status: "Authorized", effective_from: "", review_date: "", expiry_date: "", approver: "" });
  const [qualForm, setQualForm] = useState({ qualification: "", institution: "", year: "", verified: true });

  const { data: depts } = useQuery({ queryKey: ["departments"], queryFn: async () => (await api.get("/departments")).data });
  const { data: desigs } = useQuery({ queryKey: ["designations"], queryFn: async () => (await api.get("/designations")).data });
  const { data: emps } = useQuery({ queryKey: ["employeesForRM"], queryFn: async () => (await api.get("/employees")).data });
  const { data: resp } = useQuery({ queryKey: ["resp", eid], queryFn: async () => (await api.get(`/employees/${eid}/responsibilities`)).data });
  const { data: auths } = useQuery({ queryKey: ["auths", eid], queryFn: async () => (await api.get(`/employees/${eid}/authorizations`)).data });
  const { data: quals } = useQuery({ queryKey: ["quals", eid], queryFn: async () => (await api.get(`/employees/${eid}/qualifications`)).data });
  const { data: history } = useQuery({ queryKey: ["hrhist", eid], queryFn: async () => (await api.get(`/employees/${eid}/hr-history`)).data });

  const createLogin = useMutation({
    mutationFn: async () => (await api.post(`/employees/${eid}/create-login`, { password: loginPassword })).data,
    onSuccess: (d) => { setCreatedLogin({ username: d.username, password: loginPassword }); toast.success("Login created"); onChange?.(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const resetLoginPw = useMutation({
    mutationFn: async () => (await api.post(`/employees/${eid}/reset-login-password`, { password: resetPassword })).data,
    onSuccess: () => { toast.success("Password reset — share the new password with the employee"); setResetOpen(false); setResetPassword(""); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const saveChange = useMutation({
    mutationFn: async () => (await api.post(`/employees/${eid}/hr-change`, ch)).data,
    onSuccess: (d) => {
      toast.success(d.status === "Pending" ? "Change submitted for Super Admin approval" : "Change applied");
      qc.invalidateQueries({ queryKey: ["hrhist", eid] }); qc.invalidateQueries({ queryKey: ["pendingChanges"] }); onChange?.();
      setChangeOpen(false); setCh({ field_type: "", new_value: "", display_value: "", effective_from: "", reason: "" });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const addResp = useMutation({
    mutationFn: async () => (await api.post(`/employees/${eid}/responsibilities`, { text: respText, reason: "Assigned" })).data,
    onSuccess: () => { toast.success("Responsibility added"); qc.invalidateQueries({ queryKey: ["resp", eid] }); setRespText(""); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const removeResp = useMutation({
    mutationFn: async (rid) => (await api.delete(`/responsibilities/${rid}`)).data,
    onSuccess: () => { toast.success("Responsibility removed"); qc.invalidateQueries({ queryKey: ["resp", eid] }); },
  });
  const addAuth = useMutation({
    mutationFn: async () => (await api.post(`/employees/${eid}/authorizations`, authForm)).data,
    onSuccess: () => { toast.success("Authorization added"); qc.invalidateQueries({ queryKey: ["auths", eid] }); setAuthForm({ activity: "", status: "Authorized", effective_from: "", review_date: "", expiry_date: "", approver: "" }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const addQual = useMutation({
    mutationFn: async () => (await api.post(`/employees/${eid}/qualifications`, qualForm)).data,
    onSuccess: () => { toast.success("Qualification added"); qc.invalidateQueries({ queryKey: ["quals", eid] }); setQualForm({ qualification: "", institution: "", year: "", verified: true }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const selectedField = FIELD_TYPES.find((f) => f.v === ch.field_type);

  return (
    <div className="mt-6 space-y-4">
      {/* Login + effective-dated change bar */}
      <div className="flex flex-wrap items-center gap-2">
        {employee.user_id ? (
          <>
            <span className="inline-flex items-center gap-1 text-sm text-emerald-700 bg-emerald-50 rounded-full px-3 py-1"><KeyRound className="h-3.5 w-3.5" /> Login active · {employee.employee_id}</span>
            <Button variant="outline" onClick={() => setResetOpen(true)} data-testid="reset-login-btn"><RotateCcw className="h-4 w-4 mr-1" /> Reset Password</Button>
          </>
        ) : (
          <Button variant="outline" onClick={() => setLoginOpen(true)} data-testid="generate-login-btn"><KeyRound className="h-4 w-4 mr-1" /> Generate Employee Login</Button>
        )}
        <Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => setChangeOpen(true)} data-testid="hr-change-btn"><Plus className="h-4 w-4 mr-1" /> Record HR Change</Button>
      </div>

      <Tabs defaultValue="responsibilities">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="responsibilities" data-testid="hr-tab-resp">Responsibilities</TabsTrigger>
          <TabsTrigger value="authorizations" data-testid="hr-tab-auth">Authorizations</TabsTrigger>
          <TabsTrigger value="qualifications" data-testid="hr-tab-qual">Qualifications</TabsTrigger>
          <TabsTrigger value="history" data-testid="hr-tab-history">Change History</TabsTrigger>
        </TabsList>

        {/* Responsibilities */}
        <TabsContent value="responsibilities" className="mt-4 space-y-3">
          <div className="flex gap-2">
            <Input placeholder="Add a job responsibility…" value={respText} onChange={(e) => setRespText(e.target.value)} className="focus-visible:ring-teal" data-testid="resp-input" />
            <Button className="bg-navy" disabled={!respText || addResp.isPending} onClick={() => addResp.mutate()} data-testid="add-resp"><Plus className="h-4 w-4" /></Button>
          </div>
          <Card className="border-slate-200 shadow-none divide-y divide-slate-50">
            {(resp || []).length === 0 ? <p className="p-4 text-sm text-slate-400">No responsibilities yet.</p> : resp.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.status} />
                  <span className={`text-sm ${r.status === "Active" ? "text-navy" : "text-slate-400 line-through"}`}>{r.text}</span>
                </div>
                {r.status === "Active" && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeResp.mutate(r.id)} data-testid={`remove-resp-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* Authorizations */}
        <TabsContent value="authorizations" className="mt-4 space-y-3">
          <Card className="p-4 border-slate-200 shadow-none grid sm:grid-cols-2 gap-3">
            <div><Label>Activity / Equipment</Label><Input value={authForm.activity} onChange={(e) => setAuthForm({ ...authForm, activity: e.target.value })} className="mt-1" data-testid="auth-activity" /></div>
            <div><Label>Status</Label>
              <Select value={authForm.status} onValueChange={(v) => setAuthForm({ ...authForm, status: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["Authorized", "Authorized with Restriction", "Suspended", "Withdrawn"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Effective From</Label><Input type="date" value={authForm.effective_from} onChange={(e) => setAuthForm({ ...authForm, effective_from: e.target.value })} className="mt-1" /></div>
            <div><Label>Review / Expiry Date</Label><Input type="date" value={authForm.expiry_date} onChange={(e) => setAuthForm({ ...authForm, expiry_date: e.target.value })} className="mt-1" /></div>
            <div><Label>Approved By</Label><Input value={authForm.approver} onChange={(e) => setAuthForm({ ...authForm, approver: e.target.value })} className="mt-1" /></div>
            <div className="flex items-end"><Button className="bg-navy w-full" disabled={!authForm.activity || addAuth.isPending} onClick={() => addAuth.mutate()} data-testid="add-auth"><Plus className="h-4 w-4 mr-1" /> Add Authorization</Button></div>
          </Card>
          <Card className="border-slate-200 shadow-none divide-y divide-slate-50">
            {(auths || []).length === 0 ? <p className="p-4 text-sm text-slate-400">No authorizations yet.</p> : auths.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div><p className="text-sm text-navy font-medium">{a.activity}</p><p className="text-xs text-slate-400">Approver: {a.approver || "—"} · Expiry: {a.expiry_date || "—"}</p></div>
                <StatusBadge status={a.status === "Authorized" ? "Active" : a.status} />
              </div>
            ))}
          </Card>
          <p className="text-xs text-slate-400">Note: HR/job authorization is independent of application login permissions and of training/competency.</p>
        </TabsContent>

        {/* Qualifications */}
        <TabsContent value="qualifications" className="mt-4 space-y-3">
          <Card className="p-4 border-slate-200 shadow-none grid sm:grid-cols-4 gap-3 items-end">
            <div className="sm:col-span-2"><Label>Qualification</Label><Input value={qualForm.qualification} onChange={(e) => setQualForm({ ...qualForm, qualification: e.target.value })} className="mt-1" data-testid="qual-name" /></div>
            <div><Label>Institution</Label><Input value={qualForm.institution} onChange={(e) => setQualForm({ ...qualForm, institution: e.target.value })} className="mt-1" /></div>
            <div><Label>Year</Label><Input value={qualForm.year} onChange={(e) => setQualForm({ ...qualForm, year: e.target.value })} className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={qualForm.verified} onCheckedChange={(v) => setQualForm({ ...qualForm, verified: v })} /><span className="text-sm text-slate-600">Verified</span></div>
            <Button className="bg-navy sm:col-span-3" disabled={!qualForm.qualification || addQual.isPending} onClick={() => addQual.mutate()} data-testid="add-qual"><Plus className="h-4 w-4 mr-1" /> Add (append-only)</Button>
          </Card>
          <Card className="border-slate-200 shadow-none divide-y divide-slate-50">
            {(quals || []).length === 0 ? <p className="p-4 text-sm text-slate-400">No qualifications yet.</p> : quals.map((q) => (
              <div key={q.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <div><p className="text-sm text-navy font-medium">{q.qualification}</p><p className="text-xs text-slate-400">{q.institution} {q.year}</p></div>
                {q.verified && <span className="text-xs text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Verified</span>}
              </div>
            ))}
          </Card>
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-4">
          <Card className="border-slate-200 shadow-none divide-y divide-slate-50">
            <div className="flex items-center gap-2 px-4 py-3 bg-slate-50"><History className="h-4 w-4 text-teal" /><span className="overline text-slate-500">Effective-dated change history</span></div>
            {(history || []).length === 0 ? <p className="p-4 text-sm text-slate-400">No recorded changes yet.</p> : history.map((h) => (
              <div key={h.id} className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-navy">{h.field_type}</span>
                  <StatusBadge status={h.status === "Active" ? "Active" : h.status === "Pending" ? "Pending" : h.status === "Rejected" ? "Non-compliant" : "Archived"} />
                  <span className="ml-auto text-xs text-slate-400">eff. {h.effective_from}</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">{h.old_display_value || h.old_value || "—"} → <span className="text-navy">{h.display_value || h.new_value}</span></p>
                <p className="text-xs text-slate-400 mt-0.5">By {h.changed_by} · Reason: {h.reason}{h.approved_by ? ` · Approved: ${h.approved_by}` : ""}</p>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate login dialog */}
      <Dialog open={loginOpen} onOpenChange={(o) => { setLoginOpen(o); if (!o) { setCreatedLogin(null); setLoginPassword(""); } }}>
        <DialogContent className="bg-white" data-testid="login-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Generate Employee Login</DialogTitle></DialogHeader>
          {createdLogin ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Share these credentials securely with the employee.</p>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
                <p><span className="text-slate-400">Username:</span> <span className="font-mono-i text-navy">{createdLogin.username}</span></p>
                <p className="flex items-center gap-2 mt-1"><span className="text-slate-400">Password:</span> <span className="font-mono-i text-navy">{createdLogin.password}</span>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(`${createdLogin.username} / ${createdLogin.password}`); toast.success("Copied"); }}><Copy className="h-3.5 w-3.5" /></Button></p>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500 mb-3">Username will be this employee's Employee ID: <span className="font-mono-i text-navy">{employee.employee_id}</span>. Set the password they should use to sign in.</p>
              <Label>Password *</Label>
              <Input type="text" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="mt-1 font-mono-i" data-testid="login-password-input" />
              <p className="text-xs text-slate-400 mt-1">Minimum 8 characters, at least one capital letter and one special character.</p>
            </div>
          )}
          <DialogFooter>
            {createdLogin ? <Button className="bg-navy" onClick={() => { setLoginOpen(false); setCreatedLogin(null); setLoginPassword(""); }}>Done</Button> : (
              <><Button variant="outline" onClick={() => setLoginOpen(false)}>Cancel</Button>
              <Button className="bg-navy hover:bg-navy-700" disabled={!isStrongPassword(loginPassword) || createLogin.isPending} onClick={() => createLogin.mutate()} data-testid="submit-login">Create Login</Button></>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetPassword(""); }}>
        <DialogContent className="bg-white" data-testid="reset-login-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Reset Employee Password</DialogTitle></DialogHeader>
          <div>
            <p className="text-sm text-slate-500 mb-3">Set a new password for <span className="font-mono-i text-navy">{employee.employee_id}</span> and share it with the employee.</p>
            <Label>New Password *</Label>
            <Input type="text" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} className="mt-1 font-mono-i" data-testid="reset-password-input" />
            <p className="text-xs text-slate-400 mt-1">Minimum 8 characters, at least one capital letter and one special character.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!isStrongPassword(resetPassword) || resetLoginPw.isPending} onClick={() => resetLoginPw.mutate()} data-testid="submit-reset-login">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HR change dialog */}
      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent className="bg-white" data-testid="hr-change-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Record HR Change</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Effective-dated. Grade/salary & employment-status changes require Super Admin approval.</p>
          <div className="space-y-4">
            <div><Label>Field *</Label>
              <Select value={ch.field_type} onValueChange={(v) => setCh({ ...ch, field_type: v, new_value: "", display_value: "" })}>
                <SelectTrigger className="mt-1" data-testid="change-field"><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent className="bg-white">{FIELD_TYPES.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>New Value *</Label>
              {selectedField?.ref === "designations" ? (
                <Select value={ch.new_value} onValueChange={(v) => { const o = desigs.find((x) => x.id === v); setCh({ ...ch, new_value: v, display_value: o?.title }); }}>
                  <SelectTrigger className="mt-1" data-testid="change-value"><SelectValue placeholder="Select designation" /></SelectTrigger>
                  <SelectContent className="bg-white">{(desigs || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>)}</SelectContent>
                </Select>
              ) : selectedField?.ref === "departments" ? (
                <Select value={ch.new_value} onValueChange={(v) => { const o = depts.find((x) => x.id === v); setCh({ ...ch, new_value: v, display_value: o?.name }); }}>
                  <SelectTrigger className="mt-1" data-testid="change-value"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent className="bg-white">{(depts || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : selectedField?.ref === "employees" ? (
                <Select value={ch.new_value} onValueChange={(v) => { const o = (emps || []).find((x) => x.id === v); setCh({ ...ch, new_value: v, display_value: o?.identity?.name }); }}>
                  <SelectTrigger className="mt-1" data-testid="change-value"><SelectValue placeholder="Select reporting officer" /></SelectTrigger>
                  <SelectContent className="bg-white">{(emps || []).filter((o) => o.id !== eid).map((o) => <SelectItem key={o.id} value={o.id}>{o.identity?.name}{o.employee_id ? ` (${o.employee_id})` : ""}</SelectItem>)}</SelectContent>
                </Select>
              ) : ch.field_type === "employment_status" ? (
                <Select value={ch.new_value} onValueChange={(v) => setCh({ ...ch, new_value: v, display_value: v })}>
                  <SelectTrigger className="mt-1" data-testid="change-value"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent className="bg-white">{["Active", "Probation", "Confirmed", "Notice", "Left", "Terminated", "Retired"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={ch.new_value} onChange={(e) => setCh({ ...ch, new_value: e.target.value, display_value: e.target.value })} className="mt-1" data-testid="change-value" />
              )}
            </div>
            <div><Label>Effective From *</Label><Input type="date" value={ch.effective_from} onChange={(e) => setCh({ ...ch, effective_from: e.target.value })} className="mt-1" data-testid="change-effective" /></div>
            <div><Label>Reason *</Label><Textarea value={ch.reason} onChange={(e) => setCh({ ...ch, reason: e.target.value })} className="mt-1" data-testid="change-reason" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!ch.field_type || !ch.new_value || !ch.effective_from || ch.reason.length < 3 || saveChange.isPending} onClick={() => saveChange.mutate()} data-testid="submit-change">Save Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
